import { FC, useCallback } from 'react';
import { Field, FormRenderProps } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { InfoOutlined, Link } from '@mui/icons-material';
import { Box, Button, InputAdornment, Typography } from '@mui/material';

import FileUploadErrors from '@components/FormFileUploadErrors/FormFileUploadErrors';
import FileUploadField from '@components/FormFileUploadField/FileUploadField';
import FormTextField from '@components/FormTextField/FormTextField';
import RichContent from '@components/RichContent/RichContent';
import { MedicationFormFieldNames } from '@interfaces/medications';
import { IUserEnteredSupplementAddNote } from '@interfaces/supplements';
import {
  fileUploadErrorsSelector,
  fileUploadProgressSelector,
} from '@store/features/fileUploadState/selectors';
import { getParsedText } from '@views/AdminInquiriesPage/AdminInquiryList/SupplementInquiryDetails/SupplementInformationForm/utils';
import removeWhiteSpaces from '@utils/removeWhiteSpaces';

import './style.scss';

const uploadImagesSliderResponsiveSettings = [
  {
    breakpoint: 480,
    settings: {
      slidesToShow: 2,
    },
  },
  {
    breakpoint: 576,
    settings: {
      slidesToShow: 3,
    },
  },
  {
    breakpoint: 767,
    settings: {
      slidesToShow: 4,
    },
  },
  {
    breakpoint: 992,
    settings: {
      slidesToShow: 5,
    },
  },
  {
    breakpoint: 1080,
    settings: {
      slidesToShow: 7,
    },
  },
  {
    breakpoint: 10000,
    settings: {
      slidesToShow: 1,
    },
  },
];

export interface IProps extends FormRenderProps<IUserEnteredSupplementAddNote> {
  adminComments: string;
  hasUnseenResponses: boolean;
  isLastOneThatNeedsInfo: boolean;
  onClose: () => void;
}

const MedicationNeedsInfoForm: FC<IProps> = ({
  adminComments,
  handleSubmit,
  hasUnseenResponses,
  hasValidationErrors,
  isLastOneThatNeedsInfo,
  onClose,
  submitting,
}) => {
  const { t } = useTranslation();
  const fileUploadErrors = useSelector(fileUploadErrorsSelector);
  const uploadingFilesInProgress = useSelector(fileUploadProgressSelector);

  // No need to wrap with useMemo otherwise here will be unneсessary double comparison task for React
  const submitDisabled = hasValidationErrors || submitting || uploadingFilesInProgress;

  const handleSubmitClick = useCallback(
    (event: React.BaseSyntheticEvent) => {
      if (submitDisabled || !handleSubmit) {
        return;
      }
      handleSubmit(event);
    },
    [handleSubmit, submitDisabled],
  );

  // No need to wrap with useMemo otherwise here will be unneсessary double comparison task for React
  const submitLabel = isLastOneThatNeedsInfo
    ? t('inquiryResponse.resendInquiry')
    : t('common.save');

  return (
    <form className="medication-needs-info-form" onSubmit={handleSubmitClick} noValidate>
      <Typography variant="h3" className="medication-needs-info-form__title">
        {t('inquiryResponse.medicationInformationTitle')}
      </Typography>

      <p className="medication-needs-info-form__required small">{t('common.required')}</p>

      <Field
        component={FormTextField}
        className="medication-needs-info-form__input"
        label={t('addMedicationModal.medicationName')}
        name={MedicationFormFieldNames.Name}
        maxRows={2}
        format={removeWhiteSpaces}
        formatOnBlur
        multiline
        required
      />

      <Field
        component={FormTextField}
        className="medication-needs-info-form__input medication-needs-info-form__link"
        label={t('addMedicationModal.linkToProductPage')}
        name={MedicationFormFieldNames.ProductLink}
        InputProps={{
          startAdornment: (
            <InputAdornment className="medication-needs-info-form__input-icon" position="start">
              <Link color="inherit" />
            </InputAdornment>
          ),
        }}
        format={removeWhiteSpaces}
        formatOnBlur
      />

      <div className="medication-needs-info-form__admin-comments">
        {adminComments ? <RichContent>{getParsedText(adminComments)}</RichContent> : '-'}
      </div>

      <Field
        component={FormTextField}
        className="medication-needs-info-form__input medication-needs-info-form__note"
        label={t('inquiryResponse.needsInfoAddNote')}
        name={MedicationFormFieldNames.Note}
        InputProps={{
          startAdornment: (
            <InputAdornment className="medication-needs-info-form__input-icon" position="start">
              <img src="/images/img-note.svg" />
            </InputAdornment>
          ),
        }}
        rows={3}
        format={removeWhiteSpaces}
        formatOnBlur
        multiline
        required
      />

      <Field
        name={MedicationFormFieldNames.Images}
        component={FileUploadField}
        responsiveImagesSettings={uploadImagesSliderResponsiveSettings}
        slidesToShow={1}
        className="medication-needs-info-form__input"
        InputProps={{
          startAdornment: (
            <InputAdornment className="user-entered-supplement-form__input-icon" position="start">
              <Link color="inherit" />
            </InputAdornment>
          ),
        }}
        formatOnBlur
      />

      {!!fileUploadErrors.length && (
        <Box className="medication-needs-info-form__file-errors">
          <FileUploadErrors />
        </Box>
      )}

      {isLastOneThatNeedsInfo && hasUnseenResponses && (
        <p className="medication-needs-info-form__has-unseen-responses">
          <InfoOutlined />
          {t('inquiryResponse.hasUnseenResponses')}
        </p>
      )}

      <div className="medication-needs-info-form__controls">
        <Button
          className="medication-needs-info-form__submit"
          type="submit"
          variant="contained"
          disabled={submitDisabled}
        >
          <img src="/images/img-submit.svg" />
          &nbsp;&nbsp;{submitLabel}
        </Button>

        <Button className="medication-needs-info-form__cancel" variant="text" onClick={onClose}>
          {t('common.cancel')}
        </Button>
      </div>
    </form>
  );
};

export default MedicationNeedsInfoForm;
